import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import Header from '../components/Header';
import {
  TrendingUp, Target, Loader2, Crown, Sparkles, AlertTriangle, BookOpen,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUBJECT_COLORS = { Physics: '#3B82F6', Chemistry: '#10B981', Mathematics: '#8B5CF6', Biology: '#F43F5E' };

const PercentileGauge = ({ value, target }) => {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="relative w-44 h-44 mx-auto" data-testid="percentile-gauge">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#F1F5F9" strokeWidth="10" />
        <circle
          cx="60" cy="60" r="52" fill="none" stroke={pct >= target ? '#10B981' : '#0EA5E9'} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={`${(pct / 100) * 326.7} 326.7`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{value}</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">Predicted %ile</span>
      </div>
    </div>
  );
};

export default function PerformanceReport() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/performance/report`)
      .then(({ data }) => setReport(data))
      .catch((e) => { if (e.response?.status === 402) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" />
          <p className="text-sm text-[#6B7280] flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> Crunching your performance data…</p>
        </div>
      </div>
    );
  }

  // ===== Pro paywall =====
  if (locked) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="performance-paywall">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-12 text-center">
          <div className="quiz-card p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg mb-4">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Your 97+ Percentile Report</h1>
            <p className="text-sm text-[#6B7280] mb-6">
              Percentile prediction, subject-wise weak spots and a personalised AI plan that tells you exactly what to fix — updated daily from every quiz and mock you take.
            </p>
            <button onClick={() => navigate('/upgrade')} data-testid="performance-upgrade-btn"
              className="w-full px-5 py-3.5 rounded-2xl font-bold text-base bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-95 flex items-center justify-center gap-2">
              <Crown className="w-5 h-5 text-amber-300" /> Unlock with Pro — under ₹7/day
            </button>
          </div>
        </main>
      </div>
    );
  }
  if (!report) return null;

  const notEnoughData = report.total_questions < 10;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="performance-report-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-black text-[#111827] flex items-center gap-2 mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          <TrendingUp className="w-8 h-8 text-[#0EA5E9]" /> Performance Report
        </h1>
        <p className="text-sm text-[#6B7280] mb-7">Your path to {report.target_percentile}+ percentile{report.exam_track ? ` in ${report.exam_track}` : ''}.</p>

        {notEnoughData ? (
          <div className="quiz-card p-8 text-center" data-testid="not-enough-data">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="font-bold text-[#111827] mb-1">Not enough data yet</p>
            <p className="text-sm text-[#6B7280] mb-5">Attempt at least 10 questions (mocks, practice or course quizzes) to unlock your prediction.</p>
            <button onClick={() => navigate('/daily-mocks')} className="px-5 py-2.5 rounded-xl bg-[#0EA5E9] text-white text-sm font-bold hover:bg-[#0284C7]">Take today's mock →</button>
          </div>
        ) : (
          <>
            {/* Top row: gauge + gap card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div className="quiz-card p-6">
                <PercentileGauge value={report.predicted_percentile} target={report.target_percentile} />
                <p className="text-center text-xs text-[#6B7280] mt-3">
                  Based on {report.total_questions} questions across {report.tests_taken} tests
                </p>
              </div>
              <div className="quiz-card p-6 flex flex-col justify-center" data-testid="target-gap-card">
                <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-rose-500" /> Road to {report.target_percentile} %ile
                </p>
                {report.accuracy_gap > 0 ? (
                  <>
                    <p className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      +{report.accuracy_gap}% accuracy needed
                    </p>
                    <p className="text-sm text-[#6B7280] mt-1">
                      You're at <b>{report.accuracy}%</b> accuracy. Hitting ~<b>{report.accuracy_needed_for_target}%</b> consistently puts you in the 97+ percentile zone.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-black text-emerald-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>You're in the zone! 🎯</p>
                    <p className="text-sm text-[#6B7280] mt-1">Your {report.accuracy}% accuracy is at 97-percentile pace. Keep volume high to lock it in.</p>
                  </>
                )}
                <div className="mt-4 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-500" style={{ width: `${Math.min(100, (report.accuracy / report.accuracy_needed_for_target) * 100)}%` }} />
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              <div className="quiz-card p-5" data-testid="subject-accuracy-chart">
                <h3 className="text-base font-bold text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Subject Accuracy</h3>
                {report.subjects.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={report.subjects} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="subject" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} width={90} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Accuracy']} />
                      <Bar dataKey="accuracy" radius={[0, 6, 6, 0]} barSize={20}>
                        {report.subjects.map((s, i) => <Cell key={i} fill={SUBJECT_COLORS[s.subject] || '#0EA5E9'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-[200px] flex items-center justify-center text-sm text-[#9CA3AF]">Take mocks to see subject accuracy</div>}
              </div>
              <div className="quiz-card p-5" data-testid="mock-trend-chart">
                <h3 className="text-base font-bold text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Mock Accuracy Trend</h3>
                {report.trend.length > 1 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={report.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Accuracy']} />
                      <Line type="monotone" dataKey="accuracy" stroke="#0EA5E9" strokeWidth={2.5} dot={{ fill: '#0EA5E9', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="h-[200px] flex items-center justify-center text-sm text-[#9CA3AF]">Take 2+ mocks to see your trend</div>}
              </div>
            </div>

            {/* Weak topics */}
            <div className="quiz-card p-6 mb-6" data-testid="weak-topics-card">
              <h3 className="text-base font-bold text-[#111827] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Fix These First
              </h3>
              {report.weak_topics.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {report.weak_topics.map((w, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-rose-50 border border-rose-100">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-[#111827] truncate">{w.topic}</p>
                        <p className="text-[11px] text-[#6B7280]">{w.subject} · via {w.source}</p>
                      </div>
                      <span className="shrink-0 px-2.5 py-1 rounded-lg bg-rose-100 text-rose-700 text-xs font-black">{w.accuracy}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#6B7280]">No major weak spots detected yet — do more topic-tagged practice and course quizzes for a deeper drilldown.</p>
              )}
            </div>

            {/* AI plan */}
            {report.ai_plan && (
              <div className="quiz-card p-6 border-2 border-indigo-100" data-testid="ai-action-plan">
                <h3 className="text-base font-bold text-[#111827] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  <Sparkles className="w-4 h-4 text-indigo-500" /> Your AI Action Plan
                </h3>
                <div className="prose prose-sm prose-slate max-w-none prose-headings:font-black prose-li:my-0.5">
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}>{report.ai_plan}</ReactMarkdown>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
